// Central env reader (ADR-0021/D5). The rest of the code never
// touches import.meta.env directly — it imports this frozen object.
// The one sanctioned exception is src/lib/env/devRoutes.ts: it reads
// import.meta.env.DEV / VITE_APP_ENV directly and deliberately, because
// this module's computed-key read() defeats Vite's static literal
// substitution, which would silently turn devRoutes.ts's build-time
// /dev/** strip into a runtime-only branch (BIT-74).
//
// Defaults are localhost ports so `pnpm dev:dashboard` works without
// any env-file setup (matches apps/web-svelte/vite.config.ts).
// Production builds get the real Railway URLs injected by
// scripts/web-dashboard-deploy.sh (sources infra/env/web-dashboard
// .prod.env) — or by the Cloudflare Pages env-vars panel when
// building from there. If we're in PROD mode and a value is still
// the localhost default, that's a build-time misconfiguration —
// fail fast so it doesn't surface as a fetch to localhost from a
// production bundle.

const DEFAULT_API = "http://localhost:3001";
const DEFAULT_AUTH = "http://localhost:3002";
const DEFAULT_MEDIA = "http://localhost:3003";

type RequiredEnvKey = "VITE_API_BASE_URL" | "VITE_AUTH_BASE_URL" | "VITE_MEDIA_BASE_URL";

function read(key: RequiredEnvKey, fallback: string): string {
  const value = import.meta.env[key];
  if (typeof value === "string" && value.length > 0) return value;
  if (import.meta.env.PROD) {
    throw new Error(
      `Missing required env var ${key} in a production build. Set it in infra/env/web-dashboard.prod.env or the Cloudflare Pages environment-variables panel.`
    );
  }
  return fallback;
}

const DEFAULT_PWA = "http://localhost:5173";

export const env = Object.freeze({
  apiBaseUrl: read("VITE_API_BASE_URL", DEFAULT_API),
  authBaseUrl: read("VITE_AUTH_BASE_URL", DEFAULT_AUTH),
  mediaBaseUrl: read("VITE_MEDIA_BASE_URL", DEFAULT_MEDIA),
  // Deliberately NOT read() — optional (#0487, ADR-0094 §10). read()'s
  // throw-on-PROD-if-missing is right for the three URLs above (the
  // dashboard can't function without them); it's wrong here, since a
  // missing VITE_PWA_BASE_URL would white-screen the whole app at
  // module-init time for one button on one screen. The support-access
  // entry point checks this for undefined and degrades instead.
  pwaBaseUrl:
    typeof import.meta.env["VITE_PWA_BASE_URL"] === "string" &&
    import.meta.env["VITE_PWA_BASE_URL"].length > 0
      ? import.meta.env["VITE_PWA_BASE_URL"]
      : import.meta.env.PROD
        ? undefined
        : DEFAULT_PWA,
  // PostHog (ADR-0101, pending) — same optional-key shape as pwaBaseUrl
  // above: a missing key degrades to "no telemetry", never a white
  // screen. US Cloud — region is fixed at PostHog account creation.
  posthogKey:
    typeof import.meta.env["VITE_POSTHOG_KEY"] === "string" &&
    import.meta.env["VITE_POSTHOG_KEY"].length > 0
      ? import.meta.env["VITE_POSTHOG_KEY"]
      : undefined,
  posthogHost:
    typeof import.meta.env["VITE_POSTHOG_HOST"] === "string" &&
    import.meta.env["VITE_POSTHOG_HOST"].length > 0
      ? import.meta.env["VITE_POSTHOG_HOST"]
      : "https://us.i.posthog.com",
} as const);
